/**
 * Campaign Games Tab Component — shared by every campaign type
 *
 * Shows the selected campaign's eligible games in a windowed grid so
 * large lists stay performant.
 */

import type { FC } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PRIZE_POP_CAMPAIGN_TYPE,
  REBATE_CAMPAIGN_TYPE,
  TOURNAMENTS_CAMPAIGN_TYPE,
  useCampaignTypeContext,
  useSelectedCampaignId,
  type CampaignTypes,
} from 'features/campaigns';
import { VirtualizedGamesGrid } from '../../VirtualizedGamesGrid/VirtualizedGamesGrid';
import { GameCard } from 'features/games';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import type IGameSettings from 'types/models/IGameSettings';
import { useCampaignGamesTab } from './_hooks/useCampaignGamesTab';

import styles from './GamesTab.module.css';

// Analytics section-name prefix per campaign type
const SECTION_PREFIX: Record<CampaignTypes, string> = {
  [PRIZE_POP_CAMPAIGN_TYPE]: 'CAMPAIGN',
  [TOURNAMENTS_CAMPAIGN_TYPE]: 'TOURNAMENT',
  [REBATE_CAMPAIGN_TYPE]: 'REBATE',
};

const GamesTab: FC = () => {
  const campaignType = useCampaignTypeContext();
  const selectedCampaignId = useSelectedCampaignId(campaignType);

  // Provide empty string fallback to satisfy TypeScript and Rules of Hooks
  const campaignId = selectedCampaignId ?? '';

  const { campaign, allCampaignGames } = useCampaignGamesTab(campaignType);
  const { t } = useTranslation();

  if (!campaignId) {
    return null;
  }

  if (!campaign) {
    return (
      <div className={styles.gamesTabContainer}>
        <div className={styles.loadingContainer}>
          <LoadingIndicator show={true} />
        </div>
      </div>
    );
  }

  if (allCampaignGames.length === 0) {
    return (
      <div className={styles.gamesTabContainer}>
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>{t('campaigns.no_games', 'No games available')}</div>
          <div className={styles.emptyDescription}>
            {t('campaigns.no_games_description', 'This campaign has no eligible games.')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <VirtualizedGamesGrid
      games={allCampaignGames}
      campaignId={campaignId}
      renderGame={(game, index) => (
        <GameCard
          key={`${(game as IGameSettings).gameKey}_${index}`}
          data={game as IGameSettings}
          sectionName={`${SECTION_PREFIX[campaignType]}_${campaignId}`}
          context={{
            campaignId,
            campaignName: campaign.name,
            ...(campaign.level ? { campaignLevel: campaign.level } : {}),
          }}
        />
      )}
    />
  );
};

export default memo(GamesTab);
